// src/components/quizzes/BonusAddSubtractFractions.js
//
// Add & subtract fractions bonus round: catch the answer.
// Runs on the shared bonus-round engine (kit/BonusShell.js) — the
// BonusDiamonds look, bombs, bonus stars, clocks, combos, a new question
// every few right answers, and 🏁 End game. Same contract as before:
// BonusRouter renders <ThisGame onBonusComplete questions placeholderImage />.

import React from "react";
import BonusShell, { Frac } from "./kit/BonusShell";

const SPAWN_MS = 450;
const MIN_TARGETS_ONSCREEN = 2;     // at least this many correct options on screen
const TARGET_SPEED_SLOWDOWN = 0.9;  // correct fall a bit slower
const DEN_MIN = 3;                   // smallest denominator to generate
const DEN_MAX = 10;                  // largest denominator to generate

/** ======= HELPERS ======= */
const randomInt = (lo, hi) => lo + Math.floor(Math.random() * (hi - lo + 1));

const speakFrac = ({ n, d }) => {
  const ords = { 2:"halves",3:"thirds",4:"fourths",5:"fifths",6:"sixths",7:"sevenths",8:"eighths",9:"ninths",10:"tenths" };
  return `${n} ${ords[d] || `${d}ths`}`;
};

/** Problem object:
 * {
 *   op: '+'|'-',
 *   d: denominator,
 *   n1, n2: numerators,
 *   blankSide: 'right' shows "a/d op b/d = ____"; 'left' shows "____ = a/d op b/d"
 *   target: {n,d} result numerator/denominator
 * }
 */
const makeRandomProblem = () => {
  const d = randomInt(DEN_MIN, DEN_MAX);
  const op = Math.random() < 0.5 ? "+" : "-";
  let n1, n2;

  if (op === "+") {
    // keep result ≤ 1 (proper) to be friendlier for this game
    const maxN1 = d - 1;
    n1 = randomInt(1, maxN1);
    n2 = randomInt(1, Math.min(d - n1, d - 1));
  } else {
    // subtraction: keep non-negative
    n1 = randomInt(1, d - 1);
    n2 = randomInt(0, n1); // allow zero too
  }

  const targetNum = op === "+" ? n1 + n2 : n1 - n2; // denominator stays d
  const blankSide = Math.random() < 0.5 ? "right" : "left";
  return { op, d, n1, n2, blankSide, target: { n: targetNum, d } };
};

const speakableProblem = (p) => {
  const expr = `${speakFrac({ n: p.n1, d: p.d })} ${p.op === "+" ? "plus" : "minus"} ${speakFrac({ n: p.n2, d: p.d })}`;
  return p.blankSide === "right" ? `${expr} equals blank` : `blank equals ${expr}`;
};

const isCorrectFor = (p, f) => f.n === p.target.n && f.d === p.d;

/** Sample a correct and incorrect falling value */
const pickCorrectValue = (p) => ({ n: p.target.n, d: p.d });

const pickIncorrectValue = (p) => {
  // same denominator, different numerator (0..d)
  let n;
  // ensure variety around target
  if (Math.random() < 0.5) {
    n = p.target.n + (Math.random() < 0.5 ? -1 : 1);
  } else {
    n = randomInt(0, p.d);
  }
  n = Math.max(0, Math.min(p.d, n));
  if (n === p.target.n) {
    n = (n + 1) <= p.d ? n + 1 : (n - 1 >= 0 ? n - 1 : 0);
  }
  return { n, d: p.d };
};

// keep mix balanced and ensure some correct are present
const shouldSpawnCorrectBalanced = (p, items) => {
  const correct = items.filter(it => isCorrectFor(p, it.value)).length;
  const wrong = items.length - correct;
  if (correct < MIN_TARGETS_ONSCREEN) return true;
  if (correct < wrong) return true;
  if (wrong < correct) return false;
  return Math.random() < 0.5;
};

const bigOf = (p) => {
  const expr = (
    <>
      <Frac n={p.n1} d={p.d} /> {p.op === "-" ? "−" : "+"} <Frac n={p.n2} d={p.d} />
    </>
  );
  return p.blankSide === "right" ? (
    <>
      {expr} = ?
    </>
  ) : (
    <>
      ? = {expr}
    </>
  );
};

const AddSubtractFractionsBonus = (props) => (
  <BonusShell
    {...props}
    title="Fraction Math!"
    emoji="➕"
    sub="Add or subtract the fractions — then catch the answer."
    intro="Add and subtract fractions with the same denominator. Catch the fraction that equals the answer. Each one is worth five points. Watch out for bombs! Press Start when you are ready."
    rules={[
      { icon: "✅", text: "The right answer", pts: "+5" },
      { icon: "❌", text: "Wrong answer", pts: "-1" },
    ]}
    init={() => makeRandomProblem()}
    prompt={(p) => ({ big: bigOf(p), small: "Catch the answer!", say: `${speakableProblem(p)}.` })}
    spawn={(p, onScreen) => (shouldSpawnCorrectBalanced(p, onScreen) ? pickCorrectValue(p) : pickIncorrectValue(p))}
    check={isCorrectFor}
    label={(f) => <Frac n={f.n} d={f.d} />}
    newEvery={3}
    spawnMs={SPAWN_MS}
    slowGood={TARGET_SPEED_SLOWDOWN}
  />
);

export default AddSubtractFractionsBonus;
